import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { AngularTokenService } from '@raysuelzer/angular-token';
import { filter, takeUntil } from 'rxjs/operators';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { AuthState } from 'app/_state/auth/auth-state';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { CampaignContextState } from 'app/_state/campaign-context/campaign-context-state';
import { LogoffService } from 'auth/logoff.service';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { CreateEntityDialog } from 'entity/dialogs/create-entity/create-entity.dialog';
import { SupportDialogComponent } from 'app/main/components/support-dialog/support-dialog.component';
import { MfaService } from 'auth/services/mfa.service';

import { UserDetailsFragment } from 'generated/graphql';

@Component({
  selector: 'app-user-profile-card',
  templateUrl: './user-profile-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileCardComponent extends WithDisposableSubject() implements OnInit, OnDestroy {
  /** Indicates if card is open or not */
  isOpened = false;
  showEvents = false;

  currentCampaign: { id: string; name: string };

  get clientQueryId() {
    return this.activatedRoute.snapshot.queryParams['clientQueryId'] || 'default';
  }


  user: UserDetailsFragment;

  constructor(
    private authService: AngularTokenService,
    private store: Store<CampaignContextState & AuthState>,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private logoffService: LogoffService,
    private mfaService: MfaService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
    this.store.select(
      selectCurrentCampaign
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe(campaign => {
      this.showEvents = campaign?.calendarEventsEnabled;
    });
  }

  openCreateEntityDialog() {
    const dialogRef = CreateEntityDialog.open(this.dialog, {
      data: {
        campaignId: this.currentCampaign.id
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.id) {
        this.router.navigate(['/entity/view', result.id], {
          queryParams: { campaignId: this.currentCampaign.id }
        });
      }
    });
    this.isOpened = false;
  }

  ngOnInit(): void {
    this.store.select(
      createSelector(
        selectCurrentCampaign,
        selectCurrentUser,
        (campaign, user) => ({ campaign, user })
      )
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe(({campaign, user}) => {
      this.currentCampaign = campaign;
      this.user = user;
      this.cd.markForCheck();
    });

    // Close box when navigation occurs
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd))
      .subscribe(
        (_) => {
          this.isOpened = false;
          this.cd.markForCheck();
        });
  }

  // Send reset password link
  resetPassword() {
    this.isOpened = false;
    this.authService.resetPassword({ login: this.user.email })
      .subscribe(
        _ => {
          this.toastrService.success('Reset Password Link Sent');
          this.cd.markForCheck();
        });
  }

  // Remove MFA
  removeMfa() {
    this.mfaService.removeMfa(this.user.id).subscribe(
      _ => {
        this.toastrService.success('Multi-Factor Authentication removed');
        this.cd.markForCheck();
      });
  }

  // Open user support dialog
  openSupportDialog() {
    const panelClass = 'support-dialog';
    this.isOpened = false;
    this.dialog.open(SupportDialogComponent, {
      panelClass
    })
  }

  /**
   * Log off curent user
   */
  logOff() {
    this.isOpened = false;
    this.logoffService.logoff();
  }

  /**
   * Returns true if given url part
   * matches the current url.
   *
   * @param url Part to match
   */
  isLinkActive(url: string) {
    return this.router.url.includes(url);
  }

  showFooter() {
    this.isOpened = false;
    this.document.body.classList.add('show-footer');
  }
}
